import { useState, useEffect, useRef } from "react"
import { getVideos, VideoQuery, VideoType } from "../video"
import axios from "axios"

export interface IFetchVideo {
  videosData: VideoType[]
  loading: boolean
  error: { status: number } | null
  refresh: () => Promise<void>
  nResults: number
}

export const useVideoAPI = (
  query: VideoQuery,
  abortOnChange = true,
): IFetchVideo => {
  const [videosData, setVideos] = useState<VideoType[]>([])
  const [nResults, setNResults] = useState(0)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<{ status: number } | null>(null)
  const lastQueryId = useRef(0)

  const fetchVideos = async (queryParam: VideoQuery, resetIfNull = false) => {
    lastQueryId.current += 1
    const queryId = lastQueryId.current
    setLoading(true)
    try {
      const videoData = await getVideos(queryParam)
      if (abortOnChange && queryId !== lastQueryId.current) return
      setError(null)
      setVideos(videoData.videos)
      setNResults(videoData.nResults)
    } catch (err: unknown) {
      if (abortOnChange && queryId !== lastQueryId.current) return
      if (axios.isAxiosError(err)) {
        setError({ status: err.status || 500 })
      } else {
        setError({ status: 500 })
      }
      if (resetIfNull) setVideos([])
    } finally {
      if (!(abortOnChange && queryId !== lastQueryId.current)) setLoading(false)
    }
  }

  const refresh = async (resetIfNull = false) => {
    await fetchVideos(query, resetIfNull)
  }

  useEffect(() => {
    fetchVideos(query)
  }, [query.search, query.id])
  return { videosData, loading, error, refresh, nResults }
}
